import { useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import { Case, CaseReportSignature, User } from "pwapi";

import { CaseDiagnosis } from "custom-models";

import {
  cancelEditScreenDiagnoses,
  cancelModifyReportProcedure,
} from "shared/actions/caseDetailActions";
import { FeatureFlags } from "shared/reducers/sysReducer";

import CaseReport from "components/caseReport/CaseReport";
import { getDiagnosesObject } from "components/caseReport/utils";
import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import Spinner from "components/Spinner";
import UndoSignoutButton from "components/UndoSignoutButton";
import SignOutButton from "components/viewer/dialogs/SignOutButton";
import {
  canSignoutCase,
  isModifyingReport,
  isScreened,
} from "components/viewer/dialogs/utils";
import ApplicationState from "store/ApplicationState";

const CaseReportPage = () => {
  const dispatch = useDispatch();
  const caseDetailObject = useSelector(
    (state: ApplicationState) => state.caseDetail
  );
  const slideDetailObject = useSelector(
    (state: ApplicationState) => state.slideDetail
  );
  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );
  const currentUser: User | undefined = useSelector(
    ({ auth }: ApplicationState) => auth.user
  );
  const { userSelectedLocationCode } = useSelector(
    ({ locationCodes }: ApplicationState) => locationCodes
  );

  const [finalSignature, setFinalSignature] = useState<
    CaseReportSignature | undefined
  >();

  const isModifying = isModifyingReport(
    caseDetailObject,
    features,
    currentUser
  );

  const previewState = canSignoutCase(
    caseDetailObject,
    currentUser,
    features?.signOutInViewer
  )
    ? "signout"
    : isModifying?.source
    ? "addon"
    : "preview";

  const diagnoses = getDiagnosesObject(
    caseDetailObject.specimenDetail,
    previewState,
    currentUser
  );

  const onHandleCancelReportModification = () => {
    caseDetailObject?.id &&
      dispatch(cancelModifyReportProcedure(caseDetailObject.id));
  };

  const onHandleCancelScreenEdit = () => {
    caseDetailObject.id &&
      dispatch(cancelEditScreenDiagnoses(caseDetailObject.id));
  };

  return !caseDetailObject ? (
    <Spinner />
  ) : previewState === "signout" ? (
    <CaseReport
      caseDetailObject={caseDetailObject}
      diagnoses={diagnoses}
      currentUser={currentUser}
      features={features}
      isUnsigned
      selectedSignature={finalSignature}
      onChangeSignature={setFinalSignature}
      locationCode={userSelectedLocationCode}
      actions={
        <>
          {isScreened(caseDetailObject.specimenDetail[0]?.diagnoses) &&
            caseDetailObject.specimenDetail[0]?.diagnoses.find(
              (diag) =>
                diag.user &&
                diag.user.id === currentUser?.id &&
                !diag.is_completed
            ) && (
              <PathologyWatchButton
                color="red"
                onClick={onHandleCancelScreenEdit}
              >
                Cancel Screen Edit
              </PathologyWatchButton>
            )}
          <SignOutButton
            caseDetailObject={caseDetailObject}
            currentSlideId={slideDetailObject?.id}
            user={currentUser}
            selectedSignatureId={finalSignature?.id}
            className="SignoutButton"
          />
        </>
      }
    />
  ) : isModifying?.source ? (
    <CaseReport
      caseDetailObject={caseDetailObject}
      diagnoses={diagnoses}
      currentUser={currentUser}
      features={features}
      isUnsigned
      selectedSignature={finalSignature}
      onChangeSignature={setFinalSignature}
      useChangeAddon={isModifying.source !== CaseDiagnosis.source.CORRECTION}
      actions={
        <>
          <PathologyWatchButton
            onClick={onHandleCancelReportModification}
            color="red"
          >
            {`Cancel ${isModifying.source}`}
          </PathologyWatchButton>
          <SignOutButton
            caseDetailObject={caseDetailObject}
            currentSlideId={slideDetailObject?.id}
            user={currentUser}
            selectedSignatureId={finalSignature?.id}
            className="SignoutButton"
            label={`${
              isModifying.source === CaseDiagnosis.source.CORRECTION
                ? "Correct"
                : isModifying.source
            } Report`}
          />
        </>
      }
    />
  ) : (
    <CaseReport
      diagnoses={diagnoses}
      currentUser={currentUser}
      isUnsigned
      features={features}
      caseDetailObject={caseDetailObject}
      actions={
        caseDetailObject.status === Case.status.LIS_PENDING ? (
          <UndoSignoutButton
            currentUser={currentUser}
            caseDetail={caseDetailObject}
          />
        ) : undefined
      }
    />
  );
};

export default CaseReportPage;
